// Bulk edit: tick rows on any list (or Select all), then change one thing on all of them at once.
// The sheet shows only the fields given, each starting "unchanged"; what's filled in is saved to
// every picked item, one by one, and the ones that wouldn't save are named.
import { useState } from 'react';
import { Button, Field, MoneyInput, Sheet, useToast } from './kit';

export type BulkField =
  | { key: string; label: string; kind: 'money'; hint?: string }
  | { key: string; label: string; kind: 'number'; hint?: string; min?: number; max?: number }
  | { key: string; label: string; kind: 'text'; hint?: string; maxLength?: number }
  | { key: string; label: string; kind: 'date'; hint?: string }
  | { key: string; label: string; kind: 'yesno'; hint?: string; yes?: string; no?: string }
  | { key: string; label: string; kind: 'select'; hint?: string; options: { value: string; label: string }[] };

export interface BulkItem { id: string; label: string }

/** What was filled in, by field key: a number for money/number, a string for text/date/select, a boolean for yes/no. */
export type BulkValues = Record<string, number | string | boolean>;

export function BulkEditSheet({ title, what, items, fields, apply, onClose, onDone }: {
  title: string;
  /** "phones", "tickets", "customers": for the counts. */
  what: string;
  items: BulkItem[];
  fields: BulkField[];
  /** Saves the filled-in values to one item (throw to report it as not saved). */
  apply: (item: BulkItem, values: BulkValues) => Promise<void>;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [v, setV] = useState<BulkValues>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const set = (key: string, value: number | string | boolean | null) => setV((x) => {
    const next = { ...x };
    if (value === null || value === '') delete next[key];
    else next[key] = value;
    return next;
  });
  const anything = Object.keys(v).length > 0;
  const go = async () => {
    setBusy(true);
    let ok = 0;
    const failed: string[] = [];
    for (const it of items) {
      try {
        await apply(it, v);
        ok++;
      } catch (e) {
        failed.push(`${it.label}: ${e instanceof Error ? e.message : 'didn’t save'}`);
      }
      setDone(ok + failed.length);
    }
    setBusy(false);
    if (failed.length) toast(`${ok} saved. ${failed.length} didn’t: ${failed.slice(0, 3).join('; ')}${failed.length > 3 ? '…' : ''}`, ok ? 'info' : 'error');
    else toast(`${ok} ${what} updated.`, 'good');
    onDone();
  };
  return (
    <Sheet
      title={`${title} · ${items.length} ${what}`}
      onClose={onClose}
      wide
      footer={<><Button onClick={onClose} disabled={busy}>Cancel</Button><Button variant="primary" icon="check" disabled={!anything || busy} onClick={go}>{busy ? `Saving ${done} of ${items.length}…` : `Save ${items.length}`}</Button></>}
    >
      <p className="muted">Fill in only what should change on all {items.length}; everything else stays as it is.</p>
      <ul className="small" style={{ maxHeight: 140, overflow: 'auto', margin: 0, paddingLeft: 20 }}>{items.map((d) => <li key={d.id}>{d.label}</li>)}</ul>
      <div className="form-grid">
        {fields.map((f) => {
          const cur = v[f.key];
          if (f.kind === 'money') return <Field key={f.key} label={f.label} hint={f.hint ?? 'blank: unchanged'}><MoneyInput value={typeof cur === 'number' ? cur : null} onChange={(n) => set(f.key, n)} /></Field>;
          if (f.kind === 'number') return <Field key={f.key} label={f.label} hint={f.hint ?? 'blank: unchanged'}><input className="input" inputMode="numeric" value={typeof cur === 'number' ? String(cur) : ''} min={f.min} max={f.max} onChange={(e) => { const d = e.target.value.replace(/\D/g, ''); set(f.key, d ? Number(d) : null); }} /></Field>;
          if (f.kind === 'text') return <Field key={f.key} label={f.label} hint={f.hint ?? 'blank: unchanged'}><input className="input" value={typeof cur === 'string' ? cur : ''} maxLength={f.maxLength ?? 200} onChange={(e) => set(f.key, e.target.value)} /></Field>;
          if (f.kind === 'date') return <Field key={f.key} label={f.label} hint={f.hint ?? 'blank: unchanged'}><input className="input" type="date" value={typeof cur === 'string' ? cur : ''} onChange={(e) => set(f.key, e.target.value)} /></Field>;
          if (f.kind === 'yesno') {
            return (
              <Field key={f.key} label={f.label} hint={f.hint}>
                <select className="select" value={cur === true ? 'yes' : cur === false ? 'no' : ''} onChange={(e) => set(f.key, e.target.value === '' ? null : e.target.value === 'yes')}>
                  <option value="">Unchanged</option>
                  <option value="yes">{f.yes ?? 'Yes'}</option>
                  <option value="no">{f.no ?? 'No'}</option>
                </select>
              </Field>
            );
          }
          return (
            <Field key={f.key} label={f.label} hint={f.hint}>
              <select className="select" value={typeof cur === 'string' ? cur : ''} onChange={(e) => set(f.key, e.target.value || null)}>
                <option value="">Unchanged</option>
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          );
        })}
      </div>
    </Sheet>
  );
}

/** A pick bar's "Select all" box and the picked count, the same on every list. */
export function PickAll({ all, picked, total, onToggle, what }: { all: boolean; picked: number; total: number; onToggle: () => void; what: string }) {
  return (
    <label className="check" style={{ margin: 0 }}>
      <input type="checkbox" className="row-pick" checked={all} onChange={onToggle} aria-label="Select all" />
      {picked ? `${picked} picked` : `Select all ${total} ${what}`}
    </label>
  );
}
